import type { MutableRefObject } from "react";
import { useEffect, useState } from "react";
import {
  DisappearType,
  useLayer,
  UseLayerProps,
  type Placement,
} from "react-laag";

import "../../styles/popup/tooltip.css";

export { useTooltip, calculateInputLeft };

type TooltipReturn = {
  isOpen: boolean;
  setOpen: (open: boolean) => void;
} & UseLayerProps;

const useTooltip = (
  containerRef: MutableRefObject<HTMLElement | null | undefined>,
  /* Close tooltip if undefined. */
  onOutsideClick?: VoidFunction,
  /* Close tooltip when fully disappeared, if undefined. */
  onDisappear?: (disappearType: DisappearType) => void,
  /* Close tooltip if undefined. */
  onParentClose?: VoidFunction,
  /* 'top-center' by default. */
  placement: Placement = "top-center",
  possiblePlacements: Placement[] = [
    // default
    "top-center",
    // next to right end, keep bottom visbile at all costs !
    "right-center",
    // hide below
    "bottom-center",
  ],
  overflowContainerOverride?: boolean,
): TooltipReturn => {
  const [isOpen, setOpen] = useState<boolean>(false);

  const {
    renderLayer,
    triggerProps,
    layerProps,
    arrowProps,
    layerSide,
    triggerBounds,
  } = useLayer({
    // default to above, so below inputs are visible. eg, <FuzzyMatcherEditor />, <FuzzyFieldMatcherEditor />
    placement: placement,
    possiblePlacements: possiblePlacements,
    isOpen,
    overflowContainer:
      overflowContainerOverride !== undefined
        ? overflowContainerOverride
        : containerRef?.current
          ? true
          : false,
    // placement: 'bottom-end',
    onOutsideClick:
      onOutsideClick ??
      (() => {
        setOpen(false);
      }),
    auto: true,
    containerOffset: 11,
    arrowOffset: 9,
    // attach to button
    container: containerRef?.current ?? undefined,
    onDisappear:
      onDisappear ??
      ((disappearType) => {
        if (disappearType == "full") {
          setOpen(false);
        }
      }),
    onParentClose:
      onParentClose ??
      (() => {
        setOpen(false);
      }),
  });

  useEffect(() => {
    // call react-laag triggerRef, otherwise error !
    triggerProps?.ref(containerRef?.current);
  }, [containerRef, containerRef?.current]);

  return {
    renderLayer,
    triggerProps,
    layerProps,
    arrowProps,
    layerSide,
    triggerBounds,
    setOpen,
    isOpen,
  };
};

/**
 * Commonly used by components that hook useTooltip to calculate the correct position of the tooltip within an element's
 * style.
 * @param elem element
 * @returns
 */
const calculateInputLeft = (elem: HTMLElement) => {
  // might be dangerous to assume this element cannot be null !
  return elem.getBoundingClientRect().left ?? 0;
};
